import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Languages, Loader2, AlertCircle, Lock } from "lucide-react";
import { BrandLogo } from "@/components/common/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

// Only a same-origin path (never `//host` or a scheme) may be a return target.
function safeReturnTo(from) {
    return typeof from === "string" && from.startsWith("/") && !from.startsWith("//") ? from : "/";
}

export default function LoginPage() {
    const { me, login } = useAuth();
    const { t, toggleLang } = useI18n();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = safeReturnTo(location.state?.from);
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState("");

    if (me) return <Navigate to={returnTo} replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(identifier.trim(), password);
            toast.success(t("login_submit"));
            navigate(returnTo, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || t("login_error");
            setError(detail);
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[color:var(--bg-app)]">
            <div className="flex justify-end p-4">
                <Button variant="ghost" size="sm" onClick={toggleLang} className="gap-2">
                    <Languages className="h-4 w-4" />
                    <span className="text-sm">{t("language")}</span>
                </Button>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-16">
                <div className="w-full max-w-md">
                    <div className="flex flex-col items-center mb-6">
                        <BrandLogo className="h-24 w-24 object-contain" />
                        <h1 className="mt-4 text-2xl font-extrabold text-[color:var(--ink)]">{t("app_name")}</h1>
                        <p className="text-sm text-[color:var(--ink-muted)]">{t("company")}</p>
                    </div>

                    <div className="rounded-2xl surface border border-[color:var(--border-soft)] shadow-[var(--shadow-md)] p-6 sm:p-8">
                        <h2 className="text-lg font-bold text-[color:var(--ink)]">{t("login_title")}</h2>
                        <p className="text-sm text-[color:var(--ink-muted)] mt-1 mb-5">{t("login_subtitle")}</p>

                        {error ? (
                            <Alert
                                data-testid={OPS.login.error}
                                className="mb-4 border-[color:var(--danger)] text-[color:var(--danger)]"
                            >
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-[color:var(--danger)]">{error}</AlertDescription>
                            </Alert>
                        ) : null}

                        <form onSubmit={onSubmit} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="identifier">{t("login_identifier")}</Label>
                                <Input
                                    id="identifier"
                                    data-testid={OPS.login.identifier}
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    placeholder={t("login_identifier_ph")}
                                    autoComplete="username"
                                    dir="ltr"
                                    className="text-start"
                                    required
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="password">{t("login_password")}</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    data-testid={OPS.login.password}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                data-testid={OPS.login.submit}
                                disabled={submitting}
                                className="w-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 me-2 animate-spin" />
                                        {t("login_submitting")}
                                    </>
                                ) : (
                                    t("login_submit")
                                )}
                            </Button>
                        </form>
                    </div>
                    <p className="mt-6 text-center text-xs text-[color:var(--ink-muted)] flex items-center justify-center gap-1.5">
                        <Lock className="h-3 w-3" /> ops.shallal.me
                    </p>
                </div>
            </div>
        </div>
    );
}
